<template>
	<div class="content-container" ref="content_container">
		<component
			v-on="$listeners"
			v-bind="menuComponent.params"
			:is="menuComponent.component"
			:ref="menuComponent.ref"
			:class="menuComponent.class"
			:errorMessage="errorMessage"
			:dataInputParam="dataInputParam"
			:preventTables="preventTables"
			@scroll-to-element="scrollToElement"
		/>
	</div>
</template>

<script>
import EntityParamsList from './MenuLists/EntityParamsList/EntityParamsList.vue';
import DataTableColumnSelectionHandler from '@/components/DataInput/DataInputMenu/ContentSwitcher/DataTableColumnSelectionHandler';
import InsertDataEmojiPicker from '@/components/DataInput/DataInputMenu/ContentSwitcher/InsertDataEmojiPicker';
import PreviousStepsData from '@/components/DataInput/DataInputMenu/ContentSwitcher/PreviousStepsData';
import ProcessWorkflowPreviousStepsData from '@/components/DataInput/DataInputMenu/ContentSwitcher/ProcessWorkflowPreviousStepsData';
import FieldParamsList from '@/components/DataInput/DataInputMenu/MenuLists/FieldParamsList';
import DataInputMenuTabsFunctions from '@/components/DataInput/DataInputMenu/data-input-menu-tabs-functions';
import { DATA_INPUT_MENU_MAX_HEIGHT } from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';

export default {
	name: 'InsertDataMenuContent',
	props: {
		isLoading: { type: Boolean },
		isTableOnly: { type: Boolean },
		errorMessage: { type: String },
		searchString: { type: String },
		hasMoreData: { type: Boolean },
		listButtonText: { type: String },
		dataInputParam: { type: Object },
		useWithFormulas: { type: Boolean },
		isTableColumnInput: { type: Boolean },
		processWorkflowItems: { type: Array },
		showItemButtonFunc: { type: Function },
		isCurTabTableParams: { type: Boolean },
		allowTableSelection: { type: Boolean },
		isShowingEmojiPicker: { type: Boolean },
		allowMultipleSelection: { type: Boolean },
		preventTables: { type: Boolean },
		selectedProcessWorkflowId: { type: Number },
		listButtonBillingFeatureId: { type: String },
		isSelectParamsSelectionMode: { type: Boolean },
		isPreviousStepsSelectionMode: { type: Boolean },
		isInternalEntityParams: { type: Boolean },
		expandedItemId: { type: [String, Number, null] },
		fieldParams: { type: [Array, Object, String, Number] },
		localValue: { type: [Array, Object, String, Number, Boolean] },
		dataSourcesItems: { type: [Array, Object, String, Number, Boolean] },
		serviceSlug: { type: String },
		actionKey: { type: String },
		paramsMap: { type: Object },
	},
	components: {
		FieldParamsList,
		PreviousStepsData,
		InsertDataEmojiPicker,
		DataTableColumnSelectionHandler,
	},
	computed: {
		menuComponent() {
			if (this.isShowingEmojiPicker) {
				return this.emojiPickerConfiguration;
			} else if (this.showDataTableColumnSelection) {
				return this.tableColumnConfiguration;
			} else if (this.selectedProcessWorkflowId) {
				return this.processWorkflowConfiguration;
			} else if (this.isInternalEntityParams) {
				return this.entityParamsListConfiguration;
			} else if (this.isPreviousStepsSelectionMode) {
				return this.previosStepDataConfiguration;
			} else {
				return this.fieldParamsListConfiguration;
			}
		},
		showDataTableColumnSelection() {
			return this.isCurTabTableParams && !this.isPreviousStepsSelectionMode && this.isTableColumnInput;
		},
		emojiPickerConfiguration() {
			return {
				class: '',
				ref: 'emoji_picker',
				component: InsertDataEmojiPicker,
				params: {
					searchString: this.searchString,
				},
			};
		},
		tableColumnConfiguration() {
			// This is used to show only one table on insert data - example - Join tables join by rules
			return {
				class: 'pb-1',
				ref: 'data_table_representation',
				component: DataTableColumnSelectionHandler,
				eventToFunctionMap: DataInputMenuTabsFunctions.dataTableRepresentationFuncs,
				params: {
					fieldParams: this.fieldParams,
					searchString: this.searchString,
					overrideTableTitle: this.isTableColumnInput && 'Selected table',
				},
			};
		},
		fieldParamsListConfiguration() {
			return {
				class: '',
				ref: 'field_params_list',
				component: FieldParamsList,
				eventToFunctionMap: DataInputMenuTabsFunctions.fieldParamsFuncs,
				params: {
					isLoading: this.isLoading,
					hasMoreData: this.hasMoreData,
					fieldParams: this.fieldParams,
					searchString: this.searchString,
					selectedParams: this.localValue,
					listButtonText: this.listButtonText,
					showItemButtonFunc: this.showItemButtonFunc,
					allowMultipleSelection: this.allowMultipleSelection,
					hideValueDescription: this.isSelectParamsSelectionMode,
					listButtonBillingFeatureId: this.listButtonBillingFeatureId,
				},
			};
		},
		entityParamsListConfiguration() {
			return {
				class: '',
				ref: 'entity_params_list',
				component: EntityParamsList,
				eventToFunctionMap: DataInputMenuTabsFunctions.fieldParamsFuncs,
				params: {
					isLoading: this.isLoading,
					hasMoreData: this.hasMoreData,
					fieldParams: this.fieldParams,
					searchString: this.searchString,
					selectedParams: this.localValue,
					showItemButtonFunc: this.showItemButtonFunc,
					allowMultipleSelection: this.allowMultipleSelection,
					serviceSlug: this.serviceSlug,
					actionKey: this.actionKey,
					paramsMap: this.paramsMap,
				},
			};
		},
		previosStepDataConfiguration() {
			return {
				class: 'pb-1',
				ref: 'previous_step_data',
				component: PreviousStepsData,
				eventToFunctionMap: DataInputMenuTabsFunctions.previousStepsFuncs,
				params: {
					isTableOnly: this.isTableOnly,
					searchString: this.searchString,
					expandedItemId: this.expandedItemId,
					useWithFormulas: this.useWithFormulas,
					contentRectYAxis: this.contentRectYAxis,
					dataSourcesItems: this.dataSourcesItems,
					isTableColumnInput: this.isTableColumnInput,
					allowTableSelection: this.allowTableSelection,
					processWorkflowItems: this.processWorkflowItems,
				},
			};
		},
		processWorkflowConfiguration() {
			return {
				class: 'pb-1',
				ref: 'process_workflow_previous_step_data',
				component: ProcessWorkflowPreviousStepsData,
				eventToFunctionMap: DataInputMenuTabsFunctions.processWorkflowPreviousStepsFuncs,
				params: {
					isTableOnly: this.isTableOnly,
					searchString: this.searchString,
					expandedItemId: this.expandedItemId,
					useWithFormulas: this.useWithFormulas,
					dataSourcesItems: this.dataSourcesItems,
					contentRectYAxis: this.contentRectYAxis,
					isTableColumnInput: this.isTableColumnInput,
					allowTableSelection: this.allowTableSelection,
					processWorkflowItems: this.processWorkflowItems,
					selectedProcessWorkflowId: this.selectedProcessWorkflowId,
				},
			};
		},
	},
	methods: {
		handleEventFromParent(eventName) {
			this.triggerChildFunc(eventName, this.menuComponent.eventToFunctionMap, this.menuComponent.ref);
		},
		triggerChildFunc(eventName = '', childFuncs = {}, childRef = '') {
			const childFuncName = childFuncs[eventName];
			childFuncName && this.$refs[childRef][childFuncName] && this.$refs[childRef][childFuncName]();
		},
		scrollToElement(boundingClientRect, scrollToTop) {
			const el = this.$refs.content_container;
			const elementVerticalMarginFromMenuSides = 5;
			const contentMaxHeight = this.DATA_INPUT_MENU_MAX_HEIGHT - elementVerticalMarginFromMenuSides;

			if (scrollToTop) {
				el.scrollTop = 0;
				return;
			}
			const ammountToAdd = boundingClientRect.top - el.getBoundingClientRect().top + Math.min(boundingClientRect.height, contentMaxHeight); //calculate distance to new element we should focus on, (if its height is bigger than the available content space, scroll to its top)
			if (ammountToAdd >= contentMaxHeight || ammountToAdd < 0) {
				// Scroll only if the new element is out of the view we are currently seeing
				el.scrollTop += ammountToAdd - contentMaxHeight;
			}
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.contentRectYAxis = this.$refs.content_container?.getBoundingClientRect()?.y || 0;
		});
	},
	data: () => ({
		contentRectYAxis: 0,
		DATA_INPUT_MENU_MAX_HEIGHT,
	}),
};
</script>

<style lang="scss" scoped>
.content-container {
	max-height: 320px;
}
</style>
